import { Box, Stack } from '@mui/material';
import styles from './styles';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { homePage } from 'api/home-page';
import { ElementDocumentType } from 'components/element-viewer';
import { MenuDocument } from 'components/element-viewer/controls-panel-icon/DocumentsButton';
import { YNValues } from 'tools/types';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { instance } from 'api/api';
import { ITileInfoDocuments } from '../types';

export default function InfoDocuments({
    tile,
    preloadedData,
}: {
    tile: ITileInfoDocuments;
    preloadedData: ElementDocumentType[];
}) {
    const { t } = useBundleTranslation(['components/common/navbar/navbar_menu']);

    const { data: loadedData } = useQuery<Array<ElementDocumentType>, Error>({
        queryKey: [`tile-${tile.element_id}-documents`],
        queryFn: () => {
            if (!preloadedData) {
                return homePage.loadDocuments(tile.element_id ?? 0);
            }

            return [];
        },
    });

    const [open, setOpen] = useState(false);

    const data = preloadedData ? preloadedData : loadedData;

    const documents: Array<ElementDocumentType & { isNew?: YNValues; isUpdated?: YNValues }> = useMemo(
        () =>
            data
                ? data.map((doc) => ({
                      ...doc,
                      isNew: doc.isNewInd ? 'Y' : 'N',
                      isUpdated: doc.isUpdatedInd ? 'Y' : 'N',
                  }))
                : [],
        [data],
    );

    useEffect(() => {
        if (!open) {
            return;
        }
        instance.post('data/content/mark-docs-as-viewed', { docIds: documents.map((doc) => doc.documentId) });
    }, [open]);

    if (documents.length == 0) {
        return null;
    }

    return (
        <Stack direction="row" sx={styles.sectionWrapper}>
            <Box width={1} sx={styles.termSection} className={open ? 'expand' : 'collapse'}>
                <Stack
                    direction="row"
                    className={'term-section__header'}
                    onClick={() => {
                        setOpen((prevState) => !prevState);
                    }}
                >
                    <Box sx={styles.iconHolder}>
                        <IconMi icon="paperclip" sx={{ fontSize: '16px' }} />
                    </Box>

                    <Box flexGrow={1}>{t('content.documents')}</Box>
                    <Box sx={{ ...styles.iconHolder, ...{ mr: 0, ml: 1 } }}>
                        <IconMi icon={open ? 'chevron-down' : 'chevron-right'} sx={{ fontSize: 16 }} />
                    </Box>
                </Stack>
                <Stack direction="column" flexWrap={'wrap'} sx={{ pl: '24px' }} className={'term-section__items'}>
                    {documents.map((doc, index) => (
                        <Fragment key={doc.documentId}>
                            {(index == 0 || documents[index].documentType != documents[index - 1].documentType) && (
                                <Box sx={{ pt: index != 0 ? 1 : 0.5 }}>{doc.documentType}</Box>
                            )}
                            <Box sx={{ mt: 0.5 }}>
                                <MenuDocument doc={doc} />
                            </Box>
                        </Fragment>
                    ))}
                </Stack>
            </Box>
        </Stack>
    );
}
